import { useState } from 'react';
import { Alert, Button, Menu, PasswordInput, Stack, Tabs, Text, TextInput, UnstyledButton } from '@mantine/core';
import { PASSWORD_MIN } from '@parts/shared';
import { errorText } from '../../api';
import { login, register, useAuth } from '../../auth';
import { LANGS, setLang, useI18n } from '../../i18n';
import { IconPackage } from '../../icons';
import classes from './LoginScreen.module.css';

/** Экран входа и регистрации. Имя, указанное при регистрации, видно в истории действий. */
export function LoginScreen() {
  const { lang, t, tx } = useI18n();
  const auth = useAuth();
  const [tab, setTab] = useState<string | null>('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeat, setRepeat] = useState('');

  const doLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await login(loginValue, password);
      if (!res.ok) setError(tx(res.message));
    } catch (err) {
      setError(tx(errorText(err)));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async () => {
    setError(null);
    if (newPassword !== repeat) {
      setError(t('Пароли не совпадают.'));
      return;
    }
    setBusy(true);
    try {
      const res = await register(email, name, newPassword);
      if (!res.ok) setError(tx(res.message));
      else if ('pending' in res) {
        setInfo(tx(res.message));
        setTab('login');
        setLoginValue(email);
        setPassword('');
      }
    } catch (err) {
      setError(tx(errorText(err)));
    } finally {
      setBusy(false);
    }
  };

  const current = LANGS.find((l) => l.id === lang)!;

  return (
    <div className={classes.page}>
      <div className={classes.card}>
        <div className={classes.head}>
          <div className={classes.logo} aria-hidden>
            <IconPackage size={22} stroke={2} />
          </div>
          <div>
            <div className={classes.title}>{auth.title || t('Учёт запчастей')}</div>
            <div className={classes.sub}>{t('Запросы и закупки')}</div>
          </div>
          <Menu position="bottom-end" width={160}>
            <Menu.Target>
              <UnstyledButton className={classes.lang}>{current.short}</UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {LANGS.map((l) => (
                <Menu.Item key={l.id} onClick={() => setLang(l.id)}>
                  {l.title}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </div>

        <Tabs value={tab} onChange={(v) => { setTab(v); setError(null); }}>
          <Tabs.List grow>
            <Tabs.Tab value="login">{t('Вход')}</Tabs.Tab>
            {auth.allowRegistration && <Tabs.Tab value="register">{t('Регистрация')}</Tabs.Tab>}
          </Tabs.List>

          <Tabs.Panel value="login" pt="lg">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void doLogin();
              }}
            >
              <Stack gap="md">
                {info && (
                  <Alert color="green" variant="light">
                    {info}
                  </Alert>
                )}
                <TextInput
                  label={t('E-mail или логин')}
                  value={loginValue}
                  onChange={(e) => setLoginValue(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
                <PasswordInput
                  label={t('Пароль')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                {error && tab === 'login' && (
                  <Text size="sm" c="red">
                    {error}
                  </Text>
                )}
                <Button type="submit" loading={busy} fullWidth size="md">
                  {t('Войти')}
                </Button>
              </Stack>
            </form>
          </Tabs.Panel>

          {auth.allowRegistration && (
            <Tabs.Panel value="register" pt="lg">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void doRegister();
                }}
              >
                <Stack gap="md">
                  <TextInput
                    label={t('E-mail')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                  <TextInput
                    label={t('Имя')}
                    description={t('Оно будет видно в истории действий.')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                  <PasswordInput
                    label={t('Пароль')}
                    description={t('Не короче {n} символов.', { n: PASSWORD_MIN })}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <PasswordInput
                    label={t('Пароль ещё раз')}
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  {error && tab === 'register' && (
                    <Text size="sm" c="red">
                      {error}
                    </Text>
                  )}
                  <Button type="submit" loading={busy} fullWidth size="md">
                    {t('Отправить заявку')}
                  </Button>
                  <Text size="xs" c="dimmed" ta="center">
                    {t('Войти можно будет после того, как администратор подтвердит доступ и назначит роль.')}
                  </Text>
                </Stack>
              </form>
            </Tabs.Panel>
          )}
        </Tabs>
      </div>
    </div>
  );
}
